import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

const faqItems = [
  {
    question: 'Is BarsConnect free for bartenders?',
    answer: 'Yes. You can create and maintain your bartender profile for free.',
  },
  {
    question: 'What kind of bartender jobs can I find?',
    answer: 'You can find flexible shifts, recurring bar work, and event gigs.',
  },
  {
    question: 'How do bars contact me?',
    answer: 'Bars can discover your profile and message you directly through the platform.',
  },
];

export default function FAQPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender FAQ | BarsConnect',
      description: 'Get answers about bartender profiles, shifts, and how BarsConnect helps bartenders find jobs fast.',
      canonicalUrl: 'https://barsconnect.com/faq',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://barsconnect.com/faq' },
        ],
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="faq-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-3" data-testid="faq-page-title">BarsConnect Bartender FAQ</h1>
        <p className="text-muted-foreground mb-8" data-testid="faq-page-subtitle">
          Everything bartenders need to know to get discovered and hired faster.
        </p>

        <div className="space-y-4" data-testid="faq-items-list">
          {faqItems.map((item) => (
            <Card key={item.question} className="bg-white/5 border border-white/10" data-testid={`faq-item-${item.question}`}>
              <CardContent className="p-5">
                <h2 className="text-xl font-semibold text-white mb-2">{item.question}</h2>
                <p className="text-sm text-muted-foreground">{item.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Button asChild data-testid="faq-create-profile-btn">
            <Link to="/register/bartenders">Create Free Bartender Profile</Link>
          </Button>
          <Button asChild variant="outline" data-testid="faq-find-jobs-btn">
            <Link to="/bartender-jobs">Find Bartender Jobs</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
